import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { AdmissionCycle } from '../../types/admissions';
import CycleActionsConfirmDialog, { CycleAction } from './CycleActionsConfirmDialog';

const DONE: Record<CycleAction, string> = {
  open: 'Cycle opened', close: 'Cycle closed', reopen: 'Cycle reopened', archive: 'Cycle archived', delete: 'Draft deleted',
};

/**
 * A cycle's lifecycle actions — open, close, reopen, archive, delete — each
 * confirmed first. The cycles list and a cycle's own page share it, so the
 * wording and behaviour are the same wherever the action starts.
 * `onDone` gets the cycle as it now is (null after a delete).
 */
export const useCycleActions = (onDone: (cycle: AdmissionCycle | null) => void) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<{ cycle: AdmissionCycle; action: CycleAction } | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const request = useCallback((cycle: AdmissionCycle, action: CycleAction) => {
    setProblems([]);
    setPending({ cycle, action });
  }, []);

  const run = async () => {
    if (!pending) return;
    const { cycle, action } = pending;
    setBusy(true);
    try {
      const next = action === 'open' || action === 'reopen' ? await admissionCycleService.open(cycle._id)
        : action === 'close' ? await admissionCycleService.close(cycle._id)
        : action === 'archive' ? await admissionCycleService.archive(cycle._id)
        : (await admissionCycleService.remove(cycle._id), null);
      enqueueSnackbar(DONE[action], { variant: 'success' });
      setPending(null);
      onDone(next);
    } catch (err: any) {
      // A cycle that isn't ready to open lists what to fix inside the dialog.
      if (Array.isArray(err?.data?.problems)) setProblems(err.data.problems);
      else enqueueSnackbar(err?.message || 'That didn’t go through', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const dialog = pending ? (
    <CycleActionsConfirmDialog
      open
      cycle={pending.cycle}
      action={pending.action}
      onCancel={() => !busy && setPending(null)}
      onConfirm={run}
      problems={problems}
      busy={busy}
    />
  ) : null;

  return { request, dialog };
};
